import React, { useState, useEffect } from 'react';
import {
  ContentLayout,
  Header,
  Container,
  SpaceBetween,
  Box,
  Button,
  Table,
  TextFilter,
  Pagination,
  CollectionPreferences,
} from "@cloudscape-design/components";
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import * as api from "../api/api";

function CXSummary() {
  const { t } = useTranslation();
  const history = useHistory();
  const [loading, setLoading] = useState(false);
  const [historyItems, setHistoryItems] = useState([]);
  const [filteringText, setFilteringText] = useState("");
  const [currentPageIndex, setCurrentPageIndex] = useState(1);
  const PAGE_SIZE = 10;
  
  // Sorting state
  const [sortingColumn, setSortingColumn] = useState({ sortingColumn: "datetime" });
  const [sortingDescending, setSortingDescending] = useState(true);
  
  // Column preferences
  const [preferences, setPreferences] = useState({
    pageSize: PAGE_SIZE,
    wrapLines: true,
    visibleContent: ["datetime", "summaryName", "supervisors", "agents", "dateRange", "actions"]
  });

  // Load data when component mounts
  useEffect(() => {
    fetchHistoryData();
  }, []);

  const fetchHistoryData = async () => {
    try {
      setLoading(true);
      console.log("Obteniendo historial de análisis...");
      
      const response = await api.getSummaryHistory(); 
      console.log("Respuesta del historial:", response);
      
      if (response && response.history && Array.isArray(response.history)) {
        console.log(`Se encontraron ${response.history.length} elementos en el historial`);
        setHistoryItems(response.history);
      } else {
        console.log("No se encontraron elementos en el historial");
        setHistoryItems([]);
      }
    } catch (error) {
      console.error('Error fetching history:', error);
      setHistoryItems([]);
    } finally {
      setLoading(false);
    }
  };

  // Función helper para formatear rango de fechas
  const formatDateRange = (filters) => {
    if (!filters || !filters.startDate || !filters.endDate) return "-";
    
    try {
      const startDate = new Date(filters.startDate);
      const endDate = new Date(filters.endDate);
      const startStr = startDate.toLocaleDateString('es-ES', { 
        day: '2-digit', 
        month: '2-digit', 
        year: 'numeric' 
      });
      const endStr = endDate.toLocaleDateString('es-ES', { 
        day: '2-digit', 
        month: '2-digit', 
        year: 'numeric' 
      });
      return `${startStr}-${endStr}`;
    } catch (error) {
      return "Rango inválido";
    }
  };

  // Función helper para formatear agentes
  const formatAgents = (agent) => {
    if (!agent) return "Todos";
    if (Array.isArray(agent)) {
      return agent.length > 0 ? agent.join(", ") : "Todos";
    }
    return agent;
  };

  // Función helper para formatear supervisores
  const formatSupervisors = (supervisor) => {
    if (!supervisor) return "Todos";
    if (Array.isArray(supervisor)) {
      return supervisor.length > 0 ? supervisor.join(", ") : "Todos";
    }
    return supervisor;
  };

  // Handler para navegar a nuevo resumen
  const handleNewSummary = () => {
    history.push('/insights-calls');
  };

  // Handler placeholder para generar resumen desde estado vacío
  const handleGenerateSummary = () => {
    // TODO: Conectar con lógica real de generación de resumen
    console.log("Generar resumen desde estado vacío - placeholder");
  };

  // Handler placeholder para ver detalle
  const handleViewDetail = (item) => {
    // TODO: Navegar a /resumen-cx/:id o mostrar modal con detalles
    console.log("Ver detalle de:", item);
  };

  // Filtrar items según texto de búsqueda
  const filteredItems = filteringText
    ? historyItems.filter(
        item => {
          const searchText = filteringText.toLowerCase();
          const summaryName = (item.summaryName || "").toLowerCase();
          const supervisors = formatSupervisors(item.filters?.supervisor).toLowerCase();
          const agents = formatAgents(item.filters?.agent).toLowerCase();
          const dateRange = formatDateRange(item.filters).toLowerCase();
          const datetime = (item.datetime || "").toLowerCase();
          
          return summaryName.includes(searchText) ||
                 supervisors.includes(searchText) ||
                 agents.includes(searchText) ||
                 dateRange.includes(searchText) ||
                 datetime.includes(searchText);
        }
      )
    : historyItems;

  // Ordenar items según columna seleccionada
  const sortedItems = [...filteredItems].sort((a, b) => {
    const column = sortingColumn.sortingColumn || "datetime";
    let valueA, valueB;
    
    // Manejar columnas anidadas
    if (column === "filters.supervisor") {
      valueA = formatSupervisors(a.filters?.supervisor);
      valueB = formatSupervisors(b.filters?.supervisor);
    } else if (column === "filters.agent") {
      valueA = formatAgents(a.filters?.agent);
      valueB = formatAgents(b.filters?.agent);
    } else if (column === "datetime") {
      valueA = a.datetime || "";
      valueB = b.datetime || "";
      // Para ordenamiento de fechas
      const dateA = valueA ? new Date(valueA.replace(' ', 'T')).getTime() : 0;
      const dateB = valueB ? new Date(valueB.replace(' ', 'T')).getTime() : 0;
      if (dateA < dateB) return sortingDescending ? 1 : -1;
      if (dateA > dateB) return sortingDescending ? -1 : 1;
      return 0;
    } else {
      valueA = a[column] || "";
      valueB = b[column] || "";
    }
    
    // Para ordenamiento de texto
    if (typeof valueA === "string" && typeof valueB === "string") {
      const result = valueA.localeCompare(valueB);
      return sortingDescending ? -result : result;
    }
    
    // Para ordenamiento numérico
    if (valueA < valueB) return sortingDescending ? 1 : -1;
    if (valueA > valueB) return sortingDescending ? -1 : 1;
    return 0;
  });

  const pagesCount = Math.ceil(sortedItems.length / preferences.pageSize);
  const startIndex = (currentPageIndex - 1) * preferences.pageSize;
  const paginatedItems = sortedItems.slice(startIndex, startIndex + preferences.pageSize);

  // Formatear fecha/hora para mostrar
  const formatDateTime = (datetime) => {
    if (!datetime) return "-";
    try {
      // El backend devuelve formato "YYYY-MM-DD HH:MM:SS", parsearlo correctamente
      const date = new Date(datetime.replace(' ', 'T'));
      return new Intl.DateTimeFormat('es-ES', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      }).format(date);
    } catch (error) {
      console.error('Error formatting date:', error);
      return datetime;
    }
  };

  // Definición de columnas para la tabla
  const columnDefinitions = [
    {
      id: "datetime",
      header: t("insights.datetime", { defaultValue: "Fecha y hora" }),
      cell: item => formatDateTime(item.datetime),
      sortingField: "datetime",
      width: 170
    },
    {
      id: "summaryName",
      header: t("insights.summaryName", { defaultValue: "Nombre del resumen" }),
      cell: item => item.summaryName || "-",
      sortingField: "summaryName",
      minWidth: 200
    },
    {
      id: "supervisors",
      header: t("insights.supervisors", { defaultValue: "Supervisores" }),
      cell: item => formatSupervisors(item.filters?.supervisor),
      sortingField: "filters.supervisor",
      width: 170
    },
    {
      id: "agents",
      header: t("insights.agents", { defaultValue: "Agentes" }),
      cell: item => formatAgents(item.filters?.agent),
      sortingField: "filters.agent",
      width: 150
    },
    {
      id: "dateRange",
      header: t("insights.dateRange", { defaultValue: "Rango de fechas" }),
      cell: item => formatDateRange(item.filters),
      minWidth: 150
    },
    {
      id: "actions",
      header: t("insights.actions", { defaultValue: "Acciones" }),
      cell: item => (
        <Button
          variant="link"
          onClick={() => handleViewDetail(item)}
        >
          {t("insights.viewDetail", { defaultValue: "Ver detalle" })}
        </Button>
      ),
      width: 120
    }
  ];

  // Manejador de cambio de ordenación
  const handleSortingChange = ({ detail }) => {
    setSortingColumn(detail);
    setSortingDescending(detail.sortingDescending);
  };

  // Manejador de cambio de preferencias
  const handlePreferencesChange = ({ detail }) => {
    setPreferences(detail);
    if (detail.pageSize !== preferences.pageSize) {
      setCurrentPageIndex(1);
    }
  };

  return (
    <ContentLayout
      header={
        <Header
          variant="h1"
          description={t("insights.cxSummaryDescription", { 
            defaultValue: "Explorá los resúmenes globales que se generaron en la herramienta. Identificá patrones, emociones y oportunidades de mejora en un solo lugar." 
          })}
          actions={
            <Button
              variant="primary"
              iconName="add-plus"
              onClick={handleNewSummary}
            >
              {t("insights.newSummary", { defaultValue: "Nuevo resumen" })}
            </Button>
          }
        >
          {t("insights.callsTitle")}
        </Header>
      }
    >
      <Container
        header={
          <Header variant="h2">
            {t("insights.generatedSummaries", { defaultValue: "Resúmenes generados" })}
          </Header>
        }
      >
        <SpaceBetween size="m">
          <TextFilter
            filteringText={filteringText}
            filteringPlaceholder={t("insights.searchSummaries", { defaultValue: "Buscar resúmenes" })}
            filteringAriaLabel={t("insights.searchSummaries", { defaultValue: "Buscar resúmenes" })}
            onChange={({ detail }) => {
              setFilteringText(detail.filteringText);
              setCurrentPageIndex(1); // Resetear a primera página al buscar
            }}
          />
          <Table
            columnDefinitions={columnDefinitions}
            items={paginatedItems}
            loading={loading}
            loadingText={t("common.loading")}
            wrapLines={preferences.wrapLines}
            resizableColumns
            sortingColumn={sortingColumn}
            sortingDescending={sortingDescending}
            onSortingChange={handleSortingChange}
            pagination={
              <Pagination
                currentPageIndex={currentPageIndex}
                pagesCount={pagesCount}
                onChange={({ detail }) => setCurrentPageIndex(detail.currentPageIndex)}
                ariaLabels={{
                  nextPageLabel: t("common.nextPage"),
                  previousPageLabel: t("common.previousPage"),
                  pageLabel: pageNumber => `${t("common.page")} ${pageNumber}`
                }}
              />
            }
            preferences={
              <CollectionPreferences
                title={t("contactTable.preferences", { defaultValue: "Preferencias" })}
                confirmLabel={t("contactTable.confirm", { defaultValue: "Confirmar" })}
                cancelLabel={t("contactTable.cancel", { defaultValue: "Cancelar" })}
                preferences={preferences}
                onConfirm={handlePreferencesChange}
                pageSizePreference={{
                  title: t("contactTable.pageSize", { defaultValue: "Tamaño de página" }),
                  options: [
                    { value: 10, label: "10 registros" },
                    { value: 20, label: "20 registros" },
                    { value: 50, label: "50 registros" }
                  ]
                }}
                wrapLinesPreference={{
                  label: t("contactTable.wrapLines", { defaultValue: "Ajustar líneas" }),
                  description: t("contactTable.wrapLinesDescription", { defaultValue: "Marca esta casilla para visualizar los títulos completos y ajustar las líneas automáticamente." })
                }}
                visibleContentPreference={{
                  title: t("contactTable.selectVisibleColumns", { defaultValue: "Seleccionar columnas visibles" }),
                  options: [
                    {
                      label: t("insights.generatedSummaries", { defaultValue: "Resúmenes generados" }),
                      options: [
                        { id: "datetime", label: t("insights.datetime", { defaultValue: "Fecha y hora" }) },
                        { id: "summaryName", label: t("insights.summaryName", { defaultValue: "Nombre del resumen" }) },
                        { id: "supervisors", label: t("insights.supervisors", { defaultValue: "Supervisores" }) },
                        { id: "agents", label: t("insights.agents", { defaultValue: "Agentes" }) },
                        { id: "dateRange", label: t("insights.dateRange", { defaultValue: "Rango de fechas" }) },
                        { id: "actions", label: t("insights.actions", { defaultValue: "Acciones" }) }
                      ]
                    }
                  ]
                }}
              />
            }
            empty={
              <Box textAlign="center" color="inherit" padding={{ vertical: "xl" }}>
                <SpaceBetween size="m">
                  <Box variant="p" fontWeight="bold" fontSize="heading-m">
                    {t("insights.noResources", { defaultValue: "Sin recursos disponibles" })}
                  </Box>
                  <Box variant="p" color="text-body-secondary">
                    {t("insights.noResourcesDescription", { 
                      defaultValue: "No hay recursos para mostrar. Genere un nuevo resumen para visualizarlo en ésta tabla." 
                    })}
                  </Box>
                  <Button
                    variant="primary"
                    onClick={handleGenerateSummary}
                  >
                    {t("insights.generateSummary")}
                  </Button>
                </SpaceBetween>
              </Box>
            }
          />
        </SpaceBetween>
      </Container>
    </ContentLayout>
  );
}

export default CXSummary;

